import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface IncidentDetail {
  readonly incidentId: string;
  readonly incidentCode: string;
  readonly title: string;
  readonly incidentCategory: string;
  readonly severity: string;
  readonly status: string;
  readonly detectedAt: string | null;
  readonly description: string | null;
}

/**
 * GR L30.03 — Incident Workspace (`/security/incidents/{id}`). The responder
 * view of a single `security_incident`: its classification and current status,
 * with the two lifecycle actions the responder needs — close the incident with
 * a resolution summary, or escalate it with a reason.
 */
export function GrIncidentDetailPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [incident, setIncident] = useState<IncidentDetail | null>(null);
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: IncidentDetail }>(`/v1/security/incidents/${id}`, { token })
      .then((r) => { if (alive) { setIncident(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, refresh]);

  async function close(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (resolutionSummary.trim().length < 3) { setNotice(t('gr30.incidentDetail.valResolution')); return; }
    try {
      await apiRequest(`/v1/security/incidents/${id}/close`, {
        token, method: 'POST', body: { resolutionSummary: resolutionSummary.trim() },
      });
      setNotice(t('gr30.incidentDetail.closed'));
      setError(null);
      setRefresh((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function escalate(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await apiRequest(`/v1/security/incidents/${id}/escalate`, {
        token, method: 'POST', body: { reason: reason.trim() },
      });
      setNotice(t('gr30.incidentDetail.escalated'));
      setError(null);
      setRefresh((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <main aria-label="incident-detail">
      {error && <p role="alert">{error}</p>}
      {notice && <p aria-label="action-notice" role="status">{notice}</p>}
      {loading ? <p>{t('gr30.incidentDetail.loading')}</p> : incident && (
        <>
          <h1 aria-label="incident-heading">{incident.title} ({incident.status})</h1>
          <p aria-label="incident-meta">{incident.incidentCode} · {incident.incidentCategory} · {incident.severity}</p>
          <form aria-label="close-form" onSubmit={close}>
            <label>
              {t('gr30.incidentDetail.labelResolution')}
              <textarea aria-label="input-resolution" value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)} />
            </label>
            <button type="submit" aria-label="close-incident">{t('gr30.incidentDetail.closeBtn')}</button>
          </form>
          <form aria-label="escalate-form" onSubmit={escalate}>
            <label>
              {t('gr30.incidentDetail.labelReason')}
              <input aria-label="input-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <button type="submit" aria-label="escalate-incident">{t('gr30.incidentDetail.escalateBtn')}</button>
          </form>
        </>
      )}
    </main>
  );
}
